import { useCallback, ChangeEvent, useMemo } from 'react';
import {
  Stack,
  Box,
  Typography,
  Pagination,
  Theme,
  SxProps,
  alpha,
  CircularProgress,
  MenuItem,
  TextField,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import type {
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
  DataGridProProps,
  GridColumnGroupingModel,
} from '@mui/x-data-grid-pro';
import { RowCard } from './RowCard';
import type { MobileCellClick } from './RowCard';
import { mergeSx } from '../../../utils/styles';
import { MobileCardHeaderItems } from './types';
import { getPageSizeOptions } from '../../../helpers';

const styles = {
  stack: {
    flex: 1,
    px: 2,
    pb: 2,
  },
  contentWrapper: {
    position: 'relative',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    rowGap: 1,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (theme: Theme) => alpha(theme.palette.background.default, 0.5),
    backdropFilter: 'blur(0.5px)',
    zIndex: 10,
    borderRadius: 1,
  },
  noDataContainer: {
    backgroundColor: 'background.light',
    borderRadius: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    py: 6,
    px: 2,
    textAlign: 'center',
  },
  noDataIcon: {
    fontSize: '1.5rem',
    color: 'text.disabled',
    mb: 1.5,
  },
  noDataText: {
    color: 'text.secondary',
    maxWidth: 300,
    fontWeight: 500,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    py: 2,
  },
  paginationBar: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    px: 1,
  },
};

const hasIsTotalsRow = (r: unknown): r is { isTotalsRow?: boolean } =>
  typeof r === 'object' && r !== null && 'isTotalsRow' in r;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type StyledMobileDataGridProps<R extends GridValidRowModel = any> = {
  rows: readonly R[];
  columns: readonly GridColDef<R>[];
  columnVisibilityModel?: GridColumnVisibilityModel;
  getRowId?: (row: R, index: number) => string | number;
  loading?: boolean;
  page?: number;
  pageSize?: number;
  pageSizeOptions?: DataGridProProps<R>['pageSizeOptions'];
  rowCount?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  hidePagination?: boolean;
  sx?: SxProps<Theme>;
  onCellClick?: DataGridProProps['onCellClick'];
  onRowClick?: DataGridProProps['onRowClick'];
  columnGroupingModel?: GridColumnGroupingModel;
  processRowUpdate?: DataGridProProps<R>['processRowUpdate'];
  mobileCardHeaderItems?: MobileCardHeaderItems<R>;
};

type OnCellClickHandler = NonNullable<DataGridProProps['onCellClick']>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const StyledMobileDataGrid = <R extends GridValidRowModel = any>({
  rows,
  columns,
  columnVisibilityModel,
  getRowId,
  loading = false,
  page = 1,
  pageSize = 25,
  pageSizeOptions,
  rowCount,
  onPageChange,
  onPageSizeChange,
  hidePagination = false,
  sx,
  onCellClick,
  onRowClick,
  columnGroupingModel,
  processRowUpdate,
  mobileCardHeaderItems,
}: StyledMobileDataGridProps<R>) => {
  const resolveVisibility = useCallback(
    (field: string) => {
      if (!columnVisibilityModel) return true;

      return columnVisibilityModel[field] !== false;
    },
    [columnVisibilityModel]
  );

  const getId = useCallback(
    (row: R, index: number): string | number => {
      if (getRowId) return getRowId(row, index);

      const rowWithId = row as Record<string, unknown>;

      return typeof rowWithId.id === 'string' || typeof rowWithId.id === 'number' ? rowWithId.id : index;
    },
    [getRowId]
  );

  const hasRows = rows && rows.length > 0;
  const totalPageCount = Math.ceil((rowCount || 0) / pageSize);
  const showBuiltInPagination = !hidePagination && totalPageCount > 1 && onPageChange;

  const handlePaginationChange = (_: ChangeEvent<unknown>, newPage: number) => {
    onPageChange?.(newPage);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newSize = Number(event.target.value);
    onPageSizeChange?.(newSize);
  };

  const mobileOnCellClick = useCallback(
    (params: Parameters<MobileCellClick<R>>[0], event: Parameters<MobileCellClick<R>>[1]) => {
      if (!onCellClick) return;

      const handler = onCellClick as OnCellClickHandler;

      handler(
        params as Parameters<OnCellClickHandler>[0],
        event as Parameters<OnCellClickHandler>[1],
        {} as Parameters<OnCellClickHandler>[2]
      );
    },
    [onCellClick]
  );

  const rowsPerPageOptions = useMemo(
    () => pageSizeOptions ?? getPageSizeOptions(rowCount || 0),
    [pageSizeOptions, rowCount]
  );

  return (
    <Stack sx={mergeSx(styles.stack, sx)}>
      <Box sx={styles.contentWrapper}>
        {loading && (
          <Box sx={styles.loadingOverlay}>
            <CircularProgress color='primary' size={32} />
          </Box>
        )}
        {!hasRows && !loading ? (
          <Box sx={styles.noDataContainer}>
            <InfoOutlined sx={styles.noDataIcon} />
            <Typography variant='body2' sx={styles.noDataText}>
              No data to display
            </Typography>
          </Box>
        ) : (
          rows.map((row, index) => {
            let rowId = getId(row, index);
            const isTotalsRow = hasIsTotalsRow(row) ? !!row.isTotalsRow : false;

            if ((rowId === undefined || rowId === null || rowId === '') && isTotalsRow) {
              rowId = 'totals';
            }

            return (
              <RowCard<R>
                key={rowId}
                row={row}
                rowId={rowId}
                columns={columns}
                isVisible={resolveVisibility}
                onCellClick={mobileOnCellClick}
                onRowClick={onRowClick}
                columnGroupingModel={columnGroupingModel}
                isTotalsRow={isTotalsRow}
                processRowUpdate={processRowUpdate}
                mobileCardHeaderItems={mobileCardHeaderItems}
              />
            );
          })
        )}
      </Box>

      {showBuiltInPagination && (
        <Box sx={styles.pagination}>
          <Box sx={styles.paginationBar}>
            {rowsPerPageOptions && (
              <TextField select size='small' value={String(pageSize)} onChange={handlePageSizeChange} label='Rows'>
                {rowsPerPageOptions.map((option) => {
                  const value = typeof option === 'number' ? option : option.value;
                  const label = typeof option === 'number' ? option : option.label;

                  return (
                    <MenuItem key={value} value={String(value)}>
                      {label}
                    </MenuItem>
                  );
                })}
              </TextField>
            )}
            <Pagination
              page={page}
              count={totalPageCount}
              onChange={handlePaginationChange}
              shape='rounded'
              size='small'
              siblingCount={0}
            />
          </Box>
        </Box>
      )}
    </Stack>
  );
};
